import React from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import App from './App'
import People from './routes/People'
import Pages from './routes/Pages'
import Pros from './routes/Pros'
import ProsMarketingPage from './routes/ProsMarketingPage'
import BusinessReader from './routes/BusinessReader'
import PeopleReader from './routes/PeopleReader'
import BookingDetailPage from './routes/BookingDetailPage'
import PeopleEdit from './routes/PeopleEdit'
import FollowListPage from './routes/people/FollowListPage'
import GiftsListPage from './routes/people/GiftsListPage'
import ReactionsListPage from './routes/people/ReactionsListPage'
import Store from './routes/Store'
import StoreDetail from './routes/StoreDetail'
import StoreListIntro from './routes/StoreListIntro'
import StoreListEditor from './routes/StoreListEditor'
import Publish from './routes/Publish'
import PublishEditor from './routes/PublishEditor'
import Login from './routes/Login'
import PeopleEditor from './routes/PeopleEditor'
import Reader from './routes/Reader'
import JobDetail from './routes/JobDetail'
import JobsHub from './routes/jobs/JobsHub'
import ProsRequests from './routes/ProsRequests'
import ProsRequestDetailPage from './routes/ProsRequestDetailPage'
import OffersListPage from './routes/OffersListPage'
import OfferDetailPage from './routes/OfferDetailPage'
import Pricing from './routes/Pricing'
import SubscribePage from './routes/SubscribePage'
import SubscribeSuccess from './routes/SubscribeSuccess'
import SubscribeCancel from './routes/SubscribeCancel'
import SettingsLayout from './routes/settings/Layout'
import LegalSettings from './routes/settings/Legal'
import ProsConsole from './routes/ProsConsole'
import ProsConsoleGuard from './routes/ProsConsoleGuard'
import SettingsPages from './routes/settings/Pages'
import SettingsCompanies from './routes/settings/Companies'
import PageAdmin from './routes/settings/PageAdmin'
import PageEditor from './routes/settings/PageEditor'
import CreatorSubscriptions from './routes/settings/creator/Subscriptions'
import CreatorPayouts from './routes/settings/creator/Payouts'
import CreatorPlatformFeeProgramPage from './routes/settings/creator/PlatformFeeProgram'
import CreatorPixePublish from './routes/settings/creator/PixePublish'
import CreatorPixeEvents from './routes/settings/creator/PixeEvents'
import PixeEventManage from './routes/settings/creator/PixeEventManage'
import AmbassadorPage from './routes/settings/earnings/Ambassador'
import BecomeCreator from './routes/settings/BecomeCreator'
import SettingsProfile from './routes/settings/Profile'
import Placeholder from './routes/settings/Placeholder'
import AccountSettings from './routes/settings/Account'
import SecuritySettings from './routes/settings/Security'
import NotificationsSettings from './routes/settings/Notifications'
import PrivacySettings from './routes/settings/Privacy'
import AgeSettings from './routes/settings/Age'
import WalletSettings from './routes/settings/Wallet'
import WalletBuy from './routes/settings/WalletBuy'
import BillingSettings from './routes/settings/Billing'
import HandlesSettings from './routes/settings/Handles'
import PixePhotosSettings from './routes/settings/PixePhotos'
import IntegrationsSettings from './routes/settings/Integrations'
import TreasurySettings from './routes/settings/Treasury'
import TelemetryLogsSettings from './routes/settings/TelemetryLogs'
import SubscriptionsManage from './routes/settings/SubscriptionsManage'
import OwnerConsolePage from './routes/settings/OwnerConsolePage'
import SettingsModerationPage from './routes/settings/SettingsModerationPage'
import ModerationPoliciesPage from './routes/settings/ModerationPoliciesPage'
import ModerationOverview from './routes/settings/ModerationOverview'
import ModerationImageQueue from './routes/settings/ModerationImageQueue'
import SettingsFeedback from './routes/settings/Feedback'
import './index.css'
import Inbox from './routes/inbox/Inbox'
import InboxThread from './routes/inbox/Thread'
import InboxCompose from './routes/inbox/Compose'
import Signup from './routes/Signup'
import OnboardingVerify from './routes/onboarding/Verify'
import OnboardingHandlePicker from './routes/onboarding/HandlePicker'
import OnboardingReady from './routes/onboarding/Ready'
import ReferralLanding from './routes/ReferralLanding'
import WalletReceiptPage from './routes/WalletReceipt'
import AuthHandoff from './routes/auth/AuthHandoff'
import SystemMessage from './routes/inbox/SystemMessage'
import ModerationQueue from './routes/ModerationQueue'
import SuperuserOnly from './components/auth/SuperuserOnly'
import StaffOnly from './components/auth/StaffOnly'
import RequireRoot from './components/auth/RequireRoot'
import AppErrorBoundary from './components/routing/AppErrorBoundary'
import RouteError from './components/routing/RouteError'
import NotFound from './routes/NotFound'
import AdminDisputes from './routes/AdminDisputes'
import AdminDisputeDetail from './routes/AdminDisputeDetail'
import { getAppVersionMeta } from './api/meta'

if (import.meta.env.DEV) {
  // Debug overflow quickly: in devtools run `localStorage.debugOverflow="1"; location.reload()`.
  try {
    if (localStorage.getItem('debugOverflow') === '1') {
      document.documentElement.dataset.debugOverflow = '1'
    } else {
      delete document.documentElement.dataset.debugOverflow
    }
  } catch {}
}

void getAppVersionMeta()
  .then((meta) => {
    try {
      console.info('[Pixeloop] app version', meta)
    } catch {}
  })
  .catch(() => {})

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
  },
  { path: '/people', element: <People /> },
  { path: '/pages', element: <Pages /> },
  { path: '/pros', element: <ProsMarketingPage /> },
  { path: '/pros/discover', element: <Pros /> },
  { path: '/pros/console/*', element: <Navigate to="/settings/pixe-pros?tab=overview" replace /> },
  { path: '/b/:slug', element: <BusinessReader /> },
  { path: '/booking/:publicId', element: <BookingDetailPage /> },
  { path: '/people/i/:publicId', element: <PeopleReader /> },
  { path: '/people/edit', element: <PeopleEdit /> },
  { path: '/people/edit/new', element: <PeopleEditor /> },
  { path: '/store', element: <Store /> },
  { path: '/store/i/:listingId', element: <StoreDetail /> },
  { path: '/store/list', element: <StoreListIntro /> },
  { path: '/store/list/new', element: <StoreListEditor /> },
  { path: '/publish', element: <Publish /> },
  { path: '/publish/new', element: <PublishEditor /> },
  { path: '/login', element: <Login /> },
  { path: '/signup', element: <Signup /> },
  { path: '/r/:code', element: <ReferralLanding /> },
  { path: '/wallet/receipts/:entryId', element: <WalletReceiptPage /> },
  { path: '/auth/handoff', element: <AuthHandoff /> },
  { path: '/onboarding/verify', element: <OnboardingVerify /> },
  { path: '/onboarding/handlepicker', element: <OnboardingHandlePicker /> },
  { path: '/onboarding/ready', element: <OnboardingReady /> },
  { path: '/pricing', element: <Pricing /> },
  { path: '/subscribe', element: <SubscribePage /> },
  { path: '/subscribe/success', element: <SubscribeSuccess /> },
  { path: '/subscribe/cancel', element: <SubscribeCancel /> },
  { path: '/p/:handle/followers', element: <FollowListPage mode="followers" /> },
  { path: '/p/:handle/following', element: <FollowListPage mode="following" /> },
  { path: '/p/:handle/gifts', element: <GiftsListPage /> },
  { path: '/p/:handle/reactions', element: <ReactionsListPage /> },
  { path: '/p/:idSlug', element: <Reader /> },
  { path: '/jobs', element: <JobsHub /> },
  { path: '/jobs/:id', element: <JobDetail /> },
  { path: '/admin/disputes', element: <StaffOnly><AdminDisputes /></StaffOnly> },
  { path: '/admin/disputes/:jobId', element: <StaffOnly><AdminDisputeDetail /></StaffOnly> },
  { path: '/pros/requests', element: <ProsRequests /> },
  { path: '/pros/requests/:id', element: <ProsRequestDetailPage /> },
  { path: '/offers', element: <OffersListPage /> },
  { path: '/offers/:id', element: <OfferDetailPage /> },
  { path: '/inbox', element: <Inbox /> },
  { path: '/inbox/i/:threadId', element: <InboxThread /> },
  { path: '/inbox/n/:notifId', element: <SystemMessage /> },
  { path: '/inbox/compose', element: <InboxCompose /> },
  { path: '/admin/moderation', element: <Navigate to="/settings/moderation/queue" replace /> },
  {
    path: '/settings',
    element: <SettingsLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <SettingsProfile /> },
      { path: 'profile', element: <SettingsProfile /> },
      { path: 'account', element: <AccountSettings /> },
      { path: 'security', element: <SecuritySettings /> },
      {
        path: 'moderation',
        element: (
          <SuperuserOnly>
            <SettingsModerationPage />
          </SuperuserOnly>
        ),
        children: [
          { index: true, element: <ModerationOverview /> },
          {
            path: 'queue',
            element: (
              <ModerationQueue
                showPageTitle={false}
                containerClassName="px-0 py-0"
                pageClassName="min-h-full text-white"
              />
            ),
          },
          { path: 'image-queue', element: <ModerationImageQueue /> },
          { path: 'policies', element: <ModerationPoliciesPage /> },
        ],
      },
      { path: 'notifications', element: <NotificationsSettings /> },
      { path: 'privacy', element: <PrivacySettings /> },
      { path: 'age', element: <AgeSettings /> },
      { path: 'wallet', element: <WalletSettings /> },
      { path: 'wallet/buy', element: <WalletBuy /> },
      { path: 'pixe-photos', element: <PixePhotosSettings /> },
      { path: 'handles', element: <HandlesSettings /> },
      { path: 'subscriptions', element: <SubscriptionsManage /> },
      { path: 'billing', element: <BillingSettings /> },
      { path: 'treasury', element: <TreasurySettings /> },
      {
        path: 'telemetry',
        element: (
          <SuperuserOnly>
            <TelemetryLogsSettings />
          </SuperuserOnly>
        ),
      },
      {
        path: 'owner',
        element: (
          <RequireRoot>
            <OwnerConsolePage />
          </RequireRoot>
        ),
      },
      { path: 'integrations', element: <IntegrationsSettings /> },
      { path: 'legal', element: <LegalSettings /> },
      { path: 'feedback', element: <SettingsFeedback /> },
      { path: 'pixe-publish', element: <CreatorPixePublish /> },
      {
        path: 'pixe-pros',
        element: (
          <ProsConsoleGuard>
            <ProsConsole />
          </ProsConsoleGuard>
        ),
      },
      { path: 'pages', element: <SettingsPages /> },
      { path: 'companies', element: <SettingsCompanies /> },
      { path: 'pages/:slug', element: <PageAdmin /> },
      { path: 'pages/:slug/edit', element: <PageEditor /> },
      { path: 'become-a-creator', element: <BecomeCreator /> },
      { path: 'payouts', element: <CreatorPayouts /> },
      { path: 'earnings/ambassador', element: <AmbassadorPage /> },
      // Back-compat redirects (older links)
      { path: 'creator/pixe-publish', element: <Navigate to="/settings/pixe-publish" replace /> },
      { path: 'creator/pros', element: <Navigate to="/settings/pixe-pros" replace /> },
      { path: 'creator/subscriptions', element: <CreatorSubscriptions /> },
      { path: 'creator-tools/platform-fee', element: <CreatorPlatformFeeProgramPage /> },
      { path: 'creator-tools/events', element: <CreatorPixeEvents /> },
      { path: 'creator-tools/events/:postId', element: <PixeEventManage /> },
      { path: 'creator/events', element: <Navigate to="/settings/creator-tools/events" replace /> },
      { path: 'creator/payouts', element: <Navigate to="/settings/payouts" replace /> },
    ],
  },
  { path: '*', element: <NotFound /> },
])

const root = createRoot(document.getElementById('root')!)
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <RouterProvider router={router} />
    </AppErrorBoundary>
  </React.StrictMode>
)
